import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bindShareAnnotationStore, resetAnnotationStore } from '../../annotation/annotationStoreFactory';
import { getConfig } from '../../config/runtimeConfig';
import { bindShareDataSource, bindWorkspaceDataSource, resetDataSource } from '../../data/dataSourceFactory';
import { StudyPage } from '../../features/study/StudyPage';
import { aiTabsFor } from '../../features/actions/aiTab';
import { discussionDock } from '../../features/discussion/DiscussionDock';
import { prefetchNavigator } from '../../features/study/navigator/api';
import type { ShareKind } from '../../features/share/api';
import { useSession } from '../layout/TopBar';
import { routeBase, studyName, useCollectionMutations, useShareLinks } from '../api/collections';
import { imageThumbnail } from '../api/explore';
import { fetchStudyHead, studyHeadKey } from '../api/studyHead';
import { Button } from '../ui/Button';
import { ConfirmHost } from '../ui/Confirm';
import { IconArrowBack, IconChevronRight, IconHeart, IconUpload } from '../ui/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ToastHost, toast } from '../ui/toast';
import { VisChip, fromServerVisibility, inheritedNote } from '../ui/Visibility';
import { openUpload, sessionById, sessionRoute, useUpload } from '../upload/store';
import { EditDetailsButton } from '../details/EditDetailsButton';
import { useApplyViewerTheme, useViewerLight } from '../../features/viewer-controls/viewerTheme';
import { useViewerGlideSetting } from '../../features/viewer-controls/viewerGlide';
import { useViewerSharpMotionSetting, useViewerSharpnessSetting } from '../../features/viewer-controls/viewerSharpness';
import { useViewerXrayAutoWindowSetting } from '../../features/viewer-controls/xrayAutoWindow';
import { NotAvailable } from '../share/NotAvailable';
import { ViewerBottomBar } from '../../features/viewer-controls/ViewerBottomBar';
import { StudyNav, useStudySiblings, type Siblings } from './StudyNav';

/**
 * The study page as the community shell mounts it: the shared viewer with a
 * breadcrumb bar above it (← Collection › STUDY-ID · description, Upload
 * images, the visibility chip, like) and the AI and Discussion tabs beside
 * Annotations and Information. The viewer itself is untouched (it is a mockup in the
 * design; the real one stays).
 *
 * Opening a study in the viewer needs an account (decided 2026-09-15), so a
 * guest is sent to sign in; somebody without one reaches a study only through
 * its share link, which renders the same view through the link's namespace
 * (community/share/ShareRoute.tsx).
 *
 * The route names the study by public id (`/studies/stu_…`). Its images are
 * read under the study's OWN workspace (`/w/<ws>/dicom-web`), named by the
 * head: a UID is unique only inside a workspace, and the root mount reads the
 * caller's default one.
 */
export interface StudyHead {
  public_id: string;
  study_instance_uid: string;
  study_id: string | null;
  description: string | null;
  /** Effective and own. Absent through a share link, which shows no visibility controls. */
  visibility?: string;
  visibility_own?: string;
  /** The collection's visibility, for members: what it adds to the study (its menu's note). */
  collection_visibility?: string | null;
  like_count: number;
  liked: boolean;
  comment_count: number;
  /** Members: every image. Anyone else (and a share link): the published ones. */
  images: number;
  /** Members: how many images are still converting or awaiting details. */
  unpublished?: number;
  collection_id: string | null;
  collection_name: string | null;
  group_id: string | null;
  group_name: string | null;
  /**
   * The public id of the workspace the study lives in: where its DICOMweb is
   * read. Always sent by the app's head; a share link reads through its own
   * namespace and sends none.
   */
  workspace?: string | null;
  /** The same id, for members only: where an upload from here must land. */
  workspace_id?: string | null;
  /** An admin or editor of the study's workspace. */
  editable: boolean;
}

/** A study opened through a share link: the namespace every read goes to. */
export interface StudyShare {
  shareId: string;
  kind: ShareKind;
  /** The shared collection's page, when the link is a collection's: where "back" goes. */
  collectionPath?: string;
}

export function StudyPageCommunity() {
  const { studyId } = useParams<{ studyId: string }>();
  const { data: me, isLoading: sessionLoading } = useSession();
  const guest = sessionLoading ? undefined : !me?.userId;
  const head = useQuery({
    queryKey: studyHeadKey(studyId),
    queryFn: () => fetchStudyHead<StudyHead>(studyId!),
    enabled: !!studyId && guest === false,
  });
  // The studies either side of this one in its collection (the bar's previous/next).
  const siblings = useStudySiblings(head.data);
  // The viewer needs an account: the one sign-in screen, then back here.
  if (guest) return <Navigate replace to={`${routeBase()}/signin?return=${encodeURIComponent(window.location.pathname + window.location.search)}`} />;
  if (!studyId || guest === undefined || head.isLoading) return <div style={{ height: '100vh', background: 'var(--tr-page)' }} />;
  // No workspace, no way to read its images: reading the root mount instead
  // would open whatever the caller's default workspace holds under that UID.
  if (!head.data?.workspace) {
    return (
      <>
        <NotAvailable title="This study is not available" detail="It is private to another workspace, or it no longer exists." />
        <ToastHost />
      </>
    );
  }
  // Keyed by study: stepping to a neighbour (whose head is already fetched)
  // mounts a fresh viewer rather than hand the old one another study.
  return <CommunityStudyView key={head.data.public_id} head={head.data} siblings={siblings} />;
}

/**
 * The study with its bar and Discussion: from the app's own routes, or — with
 * `share` — through a share link's namespace, where there is nothing to like,
 * upload or re-share, and every read goes to /s/<shareId>.
 */
export function CommunityStudyView({
  head,
  share,
  slide,
  onSlide,
  siblings,
}: {
  head: StudyHead;
  share?: StudyShare;
  slide?: string;
  onSlide?: (seriesUid: string) => void;
  /** The collection's studies around this one, for previous/next in the bar; none outside a collection. */
  siblings?: Siblings;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data: me } = useSession();
  const guest = !me?.userId;
  const m = useCollectionMutations();
  const links = useShareLinks();
  const visWrite = useRef<Promise<unknown>>(Promise.resolve());
  // Dark unless the person chose light (Settings, or the bar at the bottom).
  const [light, setLight] = useViewerLight();
  useApplyViewerTheme(light);
  // Brings the account's glide choice into this browser, where the viewer reads it.
  useViewerGlideSetting();
  useViewerSharpnessSetting();
  // Sharpness in Motion is set in Settings, but the account's choice must reach the viewer on any device.
  useViewerSharpMotionSetting();
  // Opening X-rays on the automatic window, likewise set in Settings.
  useViewerXrayAutoWindowSetting();
  const name = studyName(head);
  // The Discussion under the panel's tabs (features/discussion): through a
  // share link it reads the link's namespace; a guest is sent to sign in and
  // back to this very page, through the link when that is how they came.
  const discussion = useMemo(
    () =>
      discussionDock({
        shareId: share?.shareId,
        signin: (go) => go(`${routeBase()}/signin?return=${encodeURIComponent(window.location.pathname + window.location.search)}`),
      }),
    [share?.shareId],
  );
  // AI between Annotations and Info: read only through a link, none for
  // somebody who reads the study only because it is Public (aiTabsFor).
  // `workspace_id` is sent to members of the study's workspace alone.
  const member = !!head.workspace_id;
  const tabs = useMemo(() => aiTabsFor({ shareId: share?.shareId, member }), [share?.shareId, member]);
  // Bind the viewer's transport BEFORE it mounts (it reaches for the data
  // source itself, several layers down): the link's namespace, or the study's
  // own workspace. Undone on the way out, so leaving never leaves the app
  // pointed at a link or at somebody else's workspace. Keyed, so opening a
  // study in another workspace from here rebinds before the viewer remounts.
  const binding = share ? `share:${share.shareId}` : `ws:${head.workspace}`;
  const [bound, setBound] = useState<string | null>(null);
  // The navigator (the viewer's image list) is read under the link's
  // namespace too, and its previews with it.
  const navigatorBase = share ? `${getConfig().apiRoot}/s/${encodeURIComponent(share.shareId)}` : getConfig().apiRoot;
  useEffect(() => {
    if (share) {
      bindShareDataSource(share.shareId);
      bindShareAnnotationStore(share.shareId);
    } else if (head.workspace) {
      bindWorkspaceDataSource(head.workspace);
    }
    // Asked for now, once the binding names its key, so it is in flight with
    // the viewer's QIDO rather than after it: the viewer waits for it before
    // opening a slide.
    void prefetchNavigator(qc, navigatorBase, head.study_instance_uid, head.public_id);
    setBound(binding);
    return () => {
      resetDataSource();
      if (share) resetAnnotationStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binding]);
  const like = () => {
    if (guest) {
      toast('Create a free account to save studies', { label: 'Create account', fn: () => nav(`${routeBase()}/signup`) });
      return;
    }
    m.like.mutate({ kind: 'study', id: head.public_id, liked: head.liked });
  };
  const back = share?.collectionPath
    ?? (head.collection_id ? `${routeBase()}/collections/${head.collection_id}` : share ? `${routeBase()}/explore` : `${routeBase()}/collections`);
  // Where the study sits, and so where "back" goes: its collection — its
  // group's band on the collection page when it is in one (#grp_…, which that
  // page scrolls to). The button names the path: the collection, then the group.
  const inCollection = share ? !!share.collectionPath : !!head.collection_id;
  const backLabel = inCollection ? head.collection_name ?? 'Collection' : share ? 'Explore' : 'Collections';
  const group = inCollection && head.group_id && head.group_name ? { id: head.group_id, name: head.group_name } : null;
  const backTo = group ? `${back}#${encodeURIComponent(group.id)}` : back;
  const backHint = group ? `Back to ${group.name} in ${backLabel}` : `Back to ${backLabel}`;
  const own = fromServerVisibility(head.visibility_own);
  const inherited = head.collection_visibility
    ? inheritedNote(own, fromServerVisibility(head.collection_visibility), head.collection_name ?? 'its collection')
    : null;
  const topBar = (
    <div className="tr-viewer-bar" style={{ display: 'flex', alignItems: 'center', gap: 12, height: 48, padding: '0 20px', background: 'var(--tr-page)', borderBottom: '1px solid var(--tr-line)', color: 'var(--tr-muted)', fontFamily: "'Instrument Sans', system-ui, sans-serif", flex: 'none' }}>
      {/* Left (user, 2026-09-30): back with its path, the study's name, then
          previous / next a little apart from it. Right: the study's buttons,
          which never move; the names on the left shrink to an ellipsis (the
          whole name on hover). On a phone the row has room for the name and
          the buttons only, so previous / next — a child of the bar, not of
          the name's box, so the bar can wrap it — take a row of their own
          under it, one at each edge (community.css, .tr-viewer-pager). */}
      <div className="tr-viewer-lead" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, minWidth: 0, height: 48, flex: '0 1 auto' }}>
        {/* Back to where the study sits: one quiet button, the arrow in the
            bar's ink and the path beside it — the collection, then the group
            (the arrow alone on a phone) — a pill under the cursor. Never
            narrower than the arrow and its padding, so a squeezed bar shrinks
            the names, not the button. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to={backTo} aria-label={backHint} className="tr-hover-track-ink" style={{ display: 'flex', alignItems: 'center', gap: 7, height: 28, padding: '0 6px', marginLeft: -6, borderRadius: 6, color: 'var(--tr-muted)', textDecoration: 'none', fontWeight: 500, minWidth: 29, maxWidth: 380, flex: '0 1 auto' }}>
              <IconArrowBack size={17} color="var(--tr-ink)" style={{ flex: 'none' }} />
              <span className="tr-ellipsis tr-hide-sm" style={{ minWidth: 40 }}>{backLabel}</span>
              {group ? (
                <>
                  <span className="tr-hide-sm" style={{ display: 'flex', flex: 'none' }}><IconChevronRight size={12} /></span>
                  <span className="tr-ellipsis tr-hide-sm" style={{ minWidth: 40 }}>{group.name}</span>
                </>
              ) : null}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="bottom">{backHint}</TooltipContent>
        </Tooltip>
        <span className="tr-ellipsis tr-viewer-name" title={name} style={{ color: 'var(--tr-ink)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, minWidth: 60, flex: '0 1 auto' }}>{name}</span>
      </div>
      {siblings ? <StudyNav {...siblings} current={head.public_id} /> : null}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: 48, flex: 'none', marginLeft: 'auto' }}>
        {/* The bar's actions are all ghost buttons (PRODUCT_RULES, Buttons):
            no border, a soft background under the cursor, like the pencil and
            the back button. Members and admins of the study's workspace see
            the pencil; nobody does through a share link. */}
        <EditDetailsButton head={head} share={share} />
        {share ? null : (
          <Button
            variant="ghost"
            size={30}
            px={9}
            gap={6}
            title={head.liked ? 'Remove from saved' : 'Save'}
            icon={<IconHeart size={13} fill={head.liked ? '#d9342b' : 'none'} color={head.liked ? '#d9342b' : 'currentColor'} />}
            onClick={(e) => {
              e.stopPropagation();
              like();
            }}
          >
            {head.like_count}
          </Button>
        )}
        {!share && head.editable && head.collection_id ? (
          <Button
            variant="ghost"
            size={30}
            px={9}
            gap={6}
            title="Upload images to this study"
            icon={<IconUpload size={13} />}
            onClick={() => {
              // Straight to the files step of THIS study: its images are
              // made under it, in its own workspace. An upload already going
              // into it is the one opened; one going anywhere else carries on.
              const id = openUpload({ colId: head.collection_id!, grpId: head.group_id, studyId: head.public_id, locationName: head.group_name ?? head.collection_name ?? '' });
              const s = sessionById(useUpload.getState().sessions, id);
              if (s) nav(sessionRoute(routeBase(), s));
            }}
          >
            <span className="tr-hide-sm">Upload</span>
          </Button>
        ) : null}
        {!share && head.editable ? (
          <VisChip
            vis={own}
            inherited={inherited}
            // A little apart from the buttons: the chip is the one outlined thing in the row.
            style={{ marginLeft: 4 }}
            onChange={(v) => { visWrite.current = m.setStudyVisibility.mutateAsync({ id: head.public_id, vis: v }).catch(() => undefined); }}
            // After any visibility change still on its way: the server only
            // hands out a link for something it already sees as Shared or Public.
            onCopy={() => void visWrite.current.then(() => links.copy('study', head.public_id, name, params.get('series')))}
            onReset={() => void links.reset('study', head.public_id, name)}
          />
        ) : null}
      </div>
    </div>
  );
  // The bar stays up while the transport is bound (a step to a neighbour
  // remounts through here): only the viewer below it waits.
  if (bound !== binding) return <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--tr-page)' }}>{topBar}</div>;
  // The viewer route sits outside the community shell, so the hosts the
  // shell provides (toasts, the confirm dialog) are mounted here: without
  // them "Upload" awaited a dialog nobody drew.
  return (
    <>
      <StudyPage
        studyUID={head.study_instance_uid}
        studyId={head.public_id}
        slide={slide}
        onSlide={onSlide}
        // Annotating takes being an admin or editor of the study's workspace.
        // A share link never grants it, whoever holds the link.
        readOnly={!!share || !head.editable}
        notify={(message) => toast(message)}
        navigatorBase={navigatorBase}
        imageThumbnail={share ? (id) => `${navigatorBase}/images/${encodeURIComponent(id)}/thumbnail` : imageThumbnail}
        topBar={topBar}
        bottomBar={<ViewerBottomBar light={light} onChange={setLight} home={{ to: `${routeBase()}/`, label: 'Home' }} />}
        // AI between Annotations and Info (tabs, above). The Discussion is
        // docked under the tabs.
        extraTabs={tabs}
        dock={discussion}
      />
      <ToastHost />
      <ConfirmHost />
    </>
  );
}
